import React, { createContext, useContext, useState, useEffect } from 'react';
import { flushSync } from 'react-dom';

const ThemeContext = createContext();

export function useTheme() {
    return useContext(ThemeContext);
}

export function ThemeProvider({ children }) {
    // Default to dark if system preference is dark, else light
    const getInitialTheme = () => {
        const saved = localStorage.getItem('theme');
        if (saved) return saved;
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    };

    const [theme, setTheme] = useState(getInitialTheme);

    const applyTheme = (newTheme) => {
        document.documentElement.setAttribute('data-theme', newTheme);
        if (newTheme === 'dark' || newTheme === 'high-contrast') {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
        localStorage.setItem('theme', newTheme);
    };

    // Apply initial theme on mount
    useEffect(() => {
        applyTheme(theme);
    }, []);

    const toggleTheme = () => {
        setTheme(prev => {
            const next = prev === 'dark' ? 'light' : 'dark';
            applyTheme(next);
            return next;
        });
    };

    const setThemeSync = (newTheme) => {
        applyTheme(newTheme);
        setTheme(newTheme);
    };

    const changeThemeWithTransition = (newTheme, originX, originY) => {
        if (!document.startViewTransition) {
            setTheme(newTheme);
            return;
        }

        const x = originX !== undefined ? originX : window.innerWidth / 2;
        const y = originY !== undefined ? originY : window.innerHeight / 2;

        const endRadius = Math.hypot(
            Math.max(x, window.innerWidth - x),
            Math.max(y, window.innerHeight - y)
        );

        const transition = document.startViewTransition(() => {
            flushSync(() => {
                setTheme(newTheme);
            });
        });

        transition.ready.then(() => {
            const clipPath = [
                `circle(0px at ${x}px ${y}px)`,
                `circle(${endRadius}px at ${x}px ${y}px)`,
            ];

            document.documentElement.animate(
                {
                    clipPath: clipPath,
                },
                {
                    duration: 600,
                    easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
                    pseudoElement: '::view-transition-new(root)',
                }
            );
        });
    };

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme, setTheme, changeThemeWithTransition }}>
            {children}
        </ThemeContext.Provider>
    );
}
